import { Box, Stack, Typography } from '@ace-clublabs/shared-component-library';

import dayjs from 'dayjs';
import { useCallback } from 'react';
import DateTimePicker from '@/components/shared/DateTimePicker';
import useAppointmentStore from '@/store/AppointmentStore';
import type { TimeSlot } from '../../utils/TimeSlot';

interface DateSelectionModuleProps {
  header: string;
  subHeader?: string;
  timeSlots: TimeSlot[];
  disabled?: boolean;
  isLoading?: boolean;
  visible?: boolean;
}

const DateSelectionModule: React.FC<DateSelectionModuleProps> = ({
  header,
  subHeader,
  timeSlots,
  disabled = false,
  isLoading = false,
  visible = true,
}) => {
  const selectedDate = useAppointmentStore((state) => state.selectedDate);
  const selectedStartTime = useAppointmentStore(
    (state) => state.selectedStartTime,
  );
  const selectedEndTime = useAppointmentStore((state) => state.selectedEndTime);
  const setSelectedDate = useAppointmentStore((state) => state.setSelectedDate);
  const setSelectedStartTime = useAppointmentStore(
    (state) => state.setSelectedStartTime,
  );
  const setSelectedEndTime = useAppointmentStore(
    (state) => state.setSelectedEndTime,
  );

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      setSelectedDate(date);
      if (date && timeSlots.length) {
        const firstSlot = timeSlots.find((slot) =>
          dayjs(slot.startTime).isSame(date, 'day'),
        );
        if (firstSlot) {
          setSelectedStartTime(firstSlot.startTime);
          setSelectedEndTime(firstSlot.endTime);
        } else {
          setSelectedStartTime('');
          setSelectedEndTime('');
        }
      } else {
        setSelectedStartTime('');
        setSelectedEndTime('');
      }
    },
    [setSelectedDate, setSelectedStartTime, setSelectedEndTime, timeSlots],
  );

  const handleTimeSlotChange = useCallback(
    (timeSlot: TimeSlot | null) => {
      setSelectedStartTime(timeSlot?.startTime || '');
      setSelectedEndTime(timeSlot?.endTime || '');
    },
    [setSelectedStartTime, setSelectedEndTime],
  );

  if (!visible) {
    return null;
  }

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        px: 1,
        pt: 2,
      }}
      spacing={3}
      data-testid={'datePickerContainer'}
    >
      <Stack>
        <Typography variant='h4'>{header}</Typography>
        {subHeader && (
          <Typography
            variant='h6'
            sx={{
              fontWeight: 500,
            }}
          >
            Book an appointment
          </Typography>
        )}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flex: {
            xs: 1,
            md: 'unset',
          },
          minHeight: {
            xs: 0,
            md: 'auto',
          },
        }}
      >
        <DateTimePicker
          timeSlots={timeSlots}
          selectedDate={selectedDate}
          selectedTimeSlot={{
            startTime: selectedStartTime || '',
            endTime: selectedEndTime || '',
          }}
          onChangeDate={handleDateChange}
          onChangeTimeSlot={handleTimeSlotChange}
          disabled={disabled}
          isLoading={isLoading || !selectedDate}
        />
      </Box>
    </Stack>
  );
};

export default DateSelectionModule;
